import { useTranslation } from "@sagot/i18n";
import { Icon, initialsOf } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { PUSH_PREVIEW_CHARS } from "./model";

interface PreviewProps {
  title: string;
  body: string;
  reach: { devices: number; emails: number } | undefined;
  reachPending: boolean;
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** Live phone lock-screen push + widget pinned message previews (mockup preview column). */
export function ComposerPreview({ title, body, reach, reachPending }: PreviewProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fmt = useFormatters();
  const now = new Date().toISOString();
  const shownTitle = title.trim() || t("engage.announcements.composer.titlePlaceholder").replace(/^e\.g\.\s*/, "");
  const shownBody = truncate(body.trim() || t("engage.announcements.composer.messagePlaceholder"), PUSH_PREVIEW_CHARS);
  const weekday = new Intl.DateTimeFormat(fmt.locale, { timeZone: fmt.timeZone, weekday: "long", month: "long", day: "numeric" }).format(new Date());
  return (
    <div className="border-t border-line bg-sunken p-5 lg:border-l lg:border-t-0" aria-live="polite">
      <div className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("engage.announcements.composer.pushPreview")}</div>
      <div className="mx-auto mt-3 w-60 rounded-[2rem] border-[6px] border-ink-950 bg-ink-800 p-3 pb-6 shadow-pop" aria-hidden="true">
        <div className="mx-auto mb-4 h-4 w-20 rounded-pill bg-ink-950" />
        <div className="text-center text-white">
          <div className="text-4xl font-semibold tabular-nums tracking-tight">{fmt.time(now).replace(/\s?[AP]M$/i, "")}</div>
          <div className="text-[11px] text-ink-300">{weekday}</div>
        </div>
        <div className="mt-6 rounded-xl bg-surface/95 p-2.5 shadow-xs">
          <div className="flex items-center gap-1.5 text-[10px] text-fg-subtle">
            <span className="inline-flex size-4 items-center justify-center rounded-xs bg-signal-500 text-[7px] font-extrabold text-ink-950">{initialsOf(summary.name)}</span>
            <span className="truncate font-semibold uppercase tracking-wide">{summary.name}</span>
            <span className="ml-auto">{t("engage.announcements.composer.previewNow")}</span>
          </div>
          <div className="mt-1 line-clamp-2 text-[12px] font-bold leading-snug text-fg">{shownTitle}</div>
          <div className="mt-0.5 line-clamp-3 text-[11px] leading-snug text-fg-muted">{shownBody}</div>
        </div>
        <div className="mt-2 rounded-xl bg-surface/60 p-2.5">
          <div className="h-2 w-24 rounded-pill bg-ink-200" />
          <div className="mt-1.5 h-2 w-36 rounded-pill bg-ink-200" />
        </div>
      </div>
      <div className="mt-5 text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{t("engage.announcements.composer.pinPreview")}</div>
      <div className="mt-2 rounded-lg border border-line bg-surface p-3" aria-hidden="true">
        <div className="flex items-start gap-2 rounded-md bg-accent-soft px-2.5 py-2">
          <Icon name="megaphone" className="mt-0.5 size-3.5 text-accent-ink" />
          <span className="text-xs font-semibold text-fg">{shownTitle}</span>
        </div>
        <div className="bubble-bot mt-2 text-xs">{t("engage.announcements.composer.previewGreeting")}</div>
      </div>
      <p className="mt-3 text-[11px] text-fg-subtle">
        {reach
          ? t("engage.announcements.composer.reach", {
              devices: t("engage.announcements.devices", { count: reach.devices }),
              emails: t("engage.announcements.composer.emails", { count: reach.emails }),
            })
          : reachPending
            ? t("engage.announcements.composer.reachPending")
            : null}
      </p>
    </div>
  );
}
